import { InputWithButton } from '@lib/components/InputWithButton'
import { IconExclamation } from '@lib/icons'
import IconX from '@lib/icons/IconX'
import { isValidIDNUrl } from '@lib/utils/strings'
import { type FC, useCallback, useRef, useState } from 'react'

import { useToggle } from '@/hooks'
import { actionUrls } from '../TextWithAction'
import css from './ExcludePathBlock.module.scss'

type Props = {
  excludedPaths: Readonly<Set<string>>
  addExcludedPath: (url: string) => Readonly<Set<string>>
  deleteExcludedPath: (url: string) => boolean
  baseUrl: string
}

enum ErrorCode {
  ROOT = 'ROOT',
  EMPTY = 'EMPTY',
  DUPLICATE = 'DUPLICATE',
  INVALID_URL = 'INVALID_URL',
}

const errorMap = new Map<ErrorCode, string>([
  [ErrorCode.ROOT, 'You entered base URL address'],
  [ErrorCode.EMPTY, "Value can't be empty"],
  [ErrorCode.DUPLICATE, 'This value has already been added'],
  [ErrorCode.INVALID_URL, "Value can't be part of url"],
])

const ExcludePathBlock: FC<Props> = ({
  excludedPaths,
  addExcludedPath,
  deleteExcludedPath,
  baseUrl,
}) => {
  const [errorCode, setErrorCode] = useState<ErrorCode | null>(null)
  const [buttonVisible, toggleButtonVisible] = useToggle()

  const inputRef = useRef<HTMLInputElement>(null)

  const onSubmit = useCallback(() => {
    if (!inputRef.current) {
      return
    }

    const { value } = inputRef.current
    let formattedValue = value.trim()

    if (formattedValue.length === 0) {
      setErrorCode(ErrorCode.EMPTY)
      return
    }

    if (formattedValue.startsWith('/')) {
      formattedValue = `/${formattedValue.replace(/^\/+|\/+$/g, '')}`
    }

    if (formattedValue === '/') {
      setErrorCode(ErrorCode.ROOT)
      return
    }

    const url = new URL(baseUrl || 'https://website.com')
    const checkUrl =
      url.origin +
      (formattedValue.startsWith('/') ? formattedValue : `/${formattedValue}`)

    if (!isValidIDNUrl(checkUrl)) {
      setErrorCode(ErrorCode.INVALID_URL)
      return
    }

    if (baseUrl && formattedValue.startsWith('/')) {
      formattedValue = checkUrl
    }

    if (excludedPaths.has(formattedValue)) {
      setErrorCode(ErrorCode.DUPLICATE)
      return
    }

    addExcludedPath(formattedValue)
    inputRef.current.value = ''
  }, [excludedPaths, baseUrl])

  const onFocus = () => {
    toggleButtonVisible(true)
  }

  const onClickAway = () => {
    if (inputRef.current && inputRef.current.value.length === 0) {
      toggleButtonVisible(false)
    }
  }

  const onChange = () => {
    setErrorCode(null)
  }

  return (
    <>
      <div className={css.subtitle}>Exclude path</div>
      <div className={css.description}>
        Add directories or keywords to create crawling exclusions
      </div>
      <div className={css.tagList}>
        {Array.from(excludedPaths).map((url) => (
          <div className={css.tag} key={url}>
            <span>{url}</span>
            <i>
              <IconX onClick={() => deleteExcludedPath(url)} size={12} />
            </i>
          </div>
        ))}
      </div>
      <InputWithButton
        ref={inputRef}
        buttonContent="Add"
        hideButton={!buttonVisible}
        placeholder={
          excludedPaths.size ? 'Add more...' : 'Enter /directory or a keyword'
        }
        onSubmit={onSubmit}
        errorText={(errorCode && errorMap.get(errorCode)) || undefined}
        onFocus={onFocus}
        onClickAway={onClickAway}
        onChange={onChange}
        data-testid="excluded_path"
      />
      <div className={css.footer}>
        <a
          className={css.footerLink}
          target="_blank"
          href={actionUrls.CRAWLER_EXCLUSIONS_URL}
          rel="noopener"
        >
          <IconExclamation className={css.footerLinkIcon} />
          More in Help center
        </a>
      </div>
    </>
  )
}

export default ExcludePathBlock
